import { zodResolver } from '@hookform/resolvers/zod';
import { orderBy } from '@/lib/collections';
import { ArrowLeft, Bike, Calendar, FileText, Plus } from 'lucide-react';
import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { useForm, type FieldError, type Resolver } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import UploadInline from '@/components/shared/upload-inline';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAddVehicle } from '@/hooks/api/use-fleet';
import { todayIso } from '@/lib/dates';
import { addFleetSchema, type AddFleetValues } from '@/schemas/add-fleet';
import type { Contract } from '@/types/api/contract';
import {
    getBrandsByTypes,
    getSizesByWheels,
    getTypesByWheels,
    getVehiculesByWheels,
    formatImmatriculation,
} from '@/lib/helpers';

/** Type de véhicule du référentiel « wheels ». */
interface WheelType {
    id: string;
    name: string;
}

interface WheelBrand {
    id: string | number;
    name: string;
}

interface WheelVehicule {
    sra: string;
    modele: string;
    version?: string;
    cylindree?: number | string;
    alimentation?: string;
    annee_debut_version?: string | number;
    annee_fin_version?: string | number | null;
}

interface FieldProps {
    label: ReactNode;
    htmlFor: string;
    error?: FieldError;
    hint?: ReactNode;
    children: ReactNode;
    required?: boolean;
}

const selectClass = 'border-input bg-transparent flex h-9 w-full rounded-md border px-4 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50';

const Field = ({ label, htmlFor, error, hint, children, required }: FieldProps) => (
    <div className="space-y-1.5">
        <label htmlFor={htmlFor} className="text-sm font-medium">
            {label}{required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        {children}
        {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
        {error && <p className="text-sm text-destructive">{error.message}</p>}
    </div>
);

export interface AddFleetProps {
    contract: Contract;
}

const AddFleet = ({ contract }: AddFleetProps) => {
    const contract_id = contract.id;
    const idSouscripteur = contract.souscripteur?.id;
    const numeroPolice = contract.numeroPolice;
    const { mutate: postAddVehicle, isPending: loading } = useAddVehicle();
    const navigate = useNavigate();
    const skipAssistance = numeroPolice === '61866386/1';

    const [types, setTypes] = useState<WheelType[]>([]);
    const [brands, setBrands] = useState<WheelBrand[]>([]);
    const [sizes, setSizes] = useState<string[]>([]);
    const [vehicules, setVehicules] = useState<WheelVehicule[]>([]);

    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<AddFleetValues>({
        defaultValues: {
            id: contract_id,
            id_tiers: idSouscripteur,
            type: '',
            marque: '',
            cylindree: '',
            vehicule: '',
            modele: '',
            sra: '',
            alimentation: '',
            date_mec: todayIso(),
            date_achat: todayIso(),
            immatriculation: '',
            assistance: skipAssistance ? 'F' : '',
            file: null,
        },
        resolver: zodResolver(addFleetSchema) as unknown as Resolver<AddFleetValues>,
    });

    const typeSelected = watch('type');
    const brandSelected = watch('marque');
    const cylindreeSelected = watch('cylindree');
    const vehiculeSelected = watch('vehicule');
    const assistanceSelected = watch('assistance');
    const file = watch('file');

    useEffect(() => {
        (async () => {
            let res = await getTypesByWheels<WheelType>();
            res = res.filter((t) => t.id !== 'quad');
            setTypes(res);
        })();
    }, []);

    const onChangeType = async (id: string) => {
        setValue('type', id, { shouldValidate: true });
        setValue('marque', '');
        setValue('cylindree', '');
        setValue('vehicule', '');
        setValue('modele', '');
        const res = await getBrandsByTypes<WheelBrand>({ type: id });
        setBrands(orderBy(res, ['name']));
        setSizes([]);
        setVehicules([]);
    };

    const onChangeBrand = async (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setValue('marque', value, { shouldValidate: true });
        setValue('cylindree', '');
        setValue('vehicule', '');
        setValue('modele', '');
        const res = await getSizesByWheels<string>({ type: typeSelected, brand: value });
        setSizes(res);
        setVehicules([]);
    };

    const onChangeSize = async (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setValue('cylindree', value, { shouldValidate: true });
        setValue('vehicule', '');
        setValue('modele', '');
        const res = await getVehiculesByWheels<WheelVehicule>({ type: typeSelected, brand: brandSelected, size: value });
        setVehicules(orderBy(res, ['modele']));
    };

    const onChangeVehicule = (e: ChangeEvent<HTMLSelectElement>) => {
        const sra = e.target.value;
        const found = vehicules.find((v) => v.sra === sra);
        setValue('vehicule', sra, { shouldValidate: true });
        if (found) {
            setValue('modele', found.modele, { shouldValidate: true });
            setValue('sra', found.sra);
            setValue('alimentation', found.alimentation as string);
        }
    };

    const onSubmit = (values: AddFleetValues) => {
        const payload = {
            ...values,
            immatriculation: formatImmatriculation((values.immatriculation || '').replaceAll(' ', '')),
        };
        postAddVehicle({
            form: payload,
            successFunction: () => navigate(`/mes-contrats/mon-contrat/${contract_id}`),
        });
    };

    return (
        <div className="space-y-6 mt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                    <Plus className="w-6 h-6 text-primary" />
                    Ajout d'un véhicule dans votre flotte
                </h1>
                <Button asChild variant="outline" size="sm">
                    <Link to={`/mes-contrats/mon-contrat/${contract_id}`}>
                        <ArrowLeft className="w-4 h-4" />
                        Retour au contrat
                    </Link>
                </Button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <input type="hidden" {...register('id')} value={contract_id} />
                <input type="hidden" {...register('id_tiers')} value={idSouscripteur || ''} />
                <input type="hidden" {...register('modele')} />
                <input type="hidden" {...register('sra')} />
                <input type="hidden" {...register('alimentation')} />

                {/* Étape 1 : choix du véhicule */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Bike className="w-5 h-5 text-primary" />
                            Véhicule
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-5">
                        <Field label="Type de véhicule" htmlFor="type" error={errors.type} required>
                            {types.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Chargement des types…</p>
                            ) : (
                                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Type de véhicule">
                                    {types.map((t) => {
                                        const active = typeSelected === t.id;
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                role="radio"
                                                aria-checked={active}
                                                onClick={() => onChangeType(t.id)}
                                                className={`px-6 py-2 rounded-md text-sm font-medium border-2 transition-colors ${
                                                    active
                                                        ? 'border-primary bg-primary text-primary-foreground'
                                                        : 'border-input bg-transparent text-foreground hover:border-primary hover:text-primary'
                                                }`}
                                            >
                                                {t.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </Field>

                        {typeSelected && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <Field label="Marque" htmlFor="marque" error={errors.marque} required>
                                    <select id="marque" className={selectClass} value={brandSelected} onChange={onChangeBrand}>
                                        <option value="">Sélectionner…</option>
                                        {brands.map((b) => (
                                            <option key={b.id} value={b.name}>{b.name}</option>
                                        ))}
                                    </select>
                                </Field>

                                <Field label="Cylindrée" htmlFor="cylindree" error={errors.cylindree} required>
                                    <select id="cylindree" className={selectClass} value={cylindreeSelected} onChange={onChangeSize} disabled={!brandSelected}>
                                        <option value="">Sélectionner…</option>
                                        {sizes.map((s, i) => (
                                            <option key={i} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </Field>

                                <Field label="Modèle" htmlFor="vehicule" error={errors.modele} required>
                                    <select id="vehicule" className={selectClass} value={vehiculeSelected} onChange={onChangeVehicule} disabled={!cylindreeSelected}>
                                        <option value="">Sélectionner…</option>
                                        {vehicules.map((v, i) => (
                                            <option key={i} value={v.sra}>
                                                {v.modele} {v.cylindree !== 50 && v.version} ({v.annee_debut_version}{v.annee_fin_version && ` - ${v.annee_fin_version}`})
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Étape 2 : détails du véhicule */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-primary" />
                            Détails du véhicule
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Date de mise en circulation" htmlFor="date_mec" error={errors.date_mec} required>
                                <Input id="date_mec" type="date" max={todayIso()} {...register('date_mec')} />
                            </Field>

                            <Field label="Date d'achat" htmlFor="date_achat" error={errors.date_achat} required>
                                <Input id="date_achat" type="date" {...register('date_achat')} />
                            </Field>
                        </div>

                        <Field
                            label="Immatriculation"
                            htmlFor="immatriculation"
                            error={errors.immatriculation}
                            hint="Format français : AA-123-AA"
                            required
                        >
                            <Input
                                id="immatriculation"
                                type="text"
                                className="uppercase font-mono"
                                placeholder="AA-123-AA"
                                {...register('immatriculation', {
                                    setValueAs: (v: string) => formatImmatriculation((v || '').replaceAll(' ', '')),
                                })}
                            />
                        </Field>

                        {!skipAssistance && (
                            <Field label="Inclure l'assistance panne 0 km ?" htmlFor="assistance" error={errors.assistance} required>
                                <div className="flex gap-2" role="radiogroup" aria-label="Assistance panne 0 km">
                                    {[
                                        { value: 'T', label: 'Oui' },
                                        { value: 'F', label: 'Non' },
                                    ].map((opt) => {
                                        const active = assistanceSelected === opt.value;
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                role="radio"
                                                aria-checked={active}
                                                onClick={() => setValue('assistance', opt.value, { shouldValidate: true })}
                                                className={`px-6 py-2 rounded-md text-sm font-medium border-2 transition-colors ${
                                                    active
                                                        ? 'border-primary bg-primary text-primary-foreground'
                                                        : 'border-input bg-transparent text-foreground hover:border-primary hover:text-primary'
                                                }`}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>
                        )}
                    </CardContent>
                </Card>

                {/* Étape 3 : document */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            Document
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <UploadInline
                            label="Carte grise"
                            code="file"
                            file={file}
                            setValue={(code, value) => setValue(code, value, { shouldValidate: true })}
                            required
                            isForm
                        />
                        {errors.file && <p className="text-sm text-destructive mt-2">{errors.file.message}</p>}
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <Button asChild variant="outline" type="button">
                        <Link to={`/mes-contrats/mon-contrat/${contract_id}`}>Annuler</Link>
                    </Button>
                    <Button
                        type="submit"
                        loading={loading}
                        loadingText="Enregistrement…"
                    >
                        <Plus className="w-4 h-4" />
                        Valider l'entrée du véhicule
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default AddFleet;
